import React, { useState, useEffect, useRef } from 'react';
import {
  addDoc, onSnapshot, orderBy, query,
  serverTimestamp, deleteDoc
} from 'firebase/firestore';
import { photosColl, albumsColl, photoDoc, albumDoc } from '../db';
import { UserProfile, Screen } from '../App';

interface Props { user: UserProfile; navigate: (s: Screen) => void; }

interface Album {
  id: string;
  name: string;
  createdBy: string;
  createdByName: string;
  timestamp: any;
}

interface Photo {
  id: string;
  url: string;
  publicId: string;
  caption: string;
  albumId: string | null;
  albumName: string | null;
  kind?: string;
  addedBy: string;
  addedByName: string;
  timestamp: any;
}

const CLOUD_NAME = process.env.REACT_APP_CLOUDINARY_CLOUD_NAME || 'dt5ndnmh6';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET || 'lovers';
const UPLOAD_URL = `https://api.cloudinary.com/v1_1/${CLOUD_NAME}/image/upload`;

async function uploadToCloudinary(file: File, userId: string, albumName: string): Promise<{ url: string; publicId: string }> {
  const folder = albumName
    ? `serhat-ayca/albums/${albumName.replace(/[^a-z0-9]/gi, '_')}`
    : `serhat-ayca/${userId}`;
  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', UPLOAD_PRESET);
  formData.append('public_id', `${folder}/${Date.now()}`);
  const res = await fetch(UPLOAD_URL, { method: 'POST', body: formData });
  if (!res.ok) throw new Error('Cloudinary yükleme hatası');
  const data = await res.json();
  return { url: data.secure_url, publicId: data.public_id };
}

type PhotoView = 'albums' | { albumId: string | null; albumName: string; kind?: string };

export default function PhotoArchive({ user, navigate }: Props) {
  const [albums, setAlbums] = useState<Album[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [view, setView] = useState<PhotoView>('albums');
  const [selected, setSelected] = useState<Photo | null>(null);
  const [showAddPhoto, setShowAddPhoto] = useState(false);
  const [showAddAlbum, setShowAddAlbum] = useState(false);
  const [caption, setCaption] = useState('');
  const [newAlbumName, setNewAlbumName] = useState('');
  const [selectedAlbumId, setSelectedAlbumId] = useState<string | null>(null);
  const [selectedAlbumName, setSelectedAlbumName] = useState('Genel');
  const [previewUrl, setPreviewUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const q1 = query(albumsColl(user.coupleId), orderBy('timestamp', 'asc'));
    const u1 = onSnapshot(q1, snap => setAlbums(snap.docs.map(d => ({ id: d.id, ...d.data() } as Album))));
    const q2 = query(photosColl(user.coupleId), orderBy('timestamp', 'desc'));
    const u2 = onSnapshot(q2, snap => {
      setPhotos(snap.docs.map(d => ({ id: d.id, ...d.data() } as Photo)));
    });
    return () => { u1(); u2(); };
  }, [user.coupleId]);

  const pickFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
    // Pre-fill album from current view
    if (typeof view === 'object' && !view.kind) {
      setSelectedAlbumId(view.albumId);
      setSelectedAlbumName(view.albumName);
    } else {
      setSelectedAlbumId(null);
      setSelectedAlbumName('Genel');
    }
    setShowAddPhoto(true);
    e.target.value = '';
  };

  const upload = async () => {
    if (!selectedFile) return;
    setUploading(true);
    setUploadStatus("Cloudinary'ye yükleniyor...");
    try {
      const { url, publicId } = await uploadToCloudinary(selectedFile, user.uid, selectedAlbumName);
      setUploadStatus('Kaydediliyor...');
      await addDoc(photosColl(user.coupleId), {
        url, publicId,
        caption: caption.trim(),
        albumId: selectedAlbumId,
        albumName: selectedAlbumId ? selectedAlbumName : null,
        addedBy: user.uid,
        addedByName: user.displayName,
        timestamp: serverTimestamp(),
      });
      closeAddPhoto();
    } catch {
      setUploadStatus('Hata oluştu, tekrar dene 😔');
      setTimeout(() => setUploadStatus(''), 3000);
    } finally {
      setUploading(false);
    }
  };

  const closeAddPhoto = () => {
    setShowAddPhoto(false); setPreviewUrl(''); setSelectedFile(null);
    setCaption(''); setUploadStatus('');
  };

  const createAlbum = async () => {
    if (!newAlbumName.trim()) return;
    await addDoc(albumsColl(user.coupleId), {
      name: newAlbumName.trim(),
      createdBy: user.uid,
      createdByName: user.displayName,
      timestamp: serverTimestamp(),
    });
    setNewAlbumName('');
    setShowAddAlbum(false);
  };

  const removePhoto = async (photo: Photo) => {
    if (!window.confirm('Bu fotoğrafı sil?')) return;
    await deleteDoc(photoDoc(user.coupleId, photo.id));
    setSelected(null);
  };

  const removeAlbum = async (albumId: string) => {
    if (!window.confirm('Klasörü sil? (Fotoğraflar silinmez)')) return;
    await deleteDoc(albumDoc(user.coupleId, albumId));
  };

  // Helpers
  const instantPhotos = photos.filter(p => p.kind === 'instant');

  const getAlbumPhotos = (albumId: string | null, kind?: string) => {
    if (kind) return photos.filter(p => p.kind === kind);
    return albumId === null ? photos : photos.filter(p => p.albumId === albumId);
  };

  const getCover = (albumId: string | null, kind?: string) =>
    getAlbumPhotos(albumId, kind)[0]?.url || null;

  const visiblePhotos = typeof view === 'object' ? getAlbumPhotos(view.albumId, view.kind) : [];

  return (
    <div>
      <div className="page-header">
        <button className="back-btn" onClick={() => view === 'albums' ? navigate('memories') : setView('albums')}>←</button>
        <h1 style={styles.title}>
          {view === 'albums' ? 'Fotoğraf Arşivi' : (view as any).albumName}
        </h1>
      </div>

      {/* Album grid view */}
      {view === 'albums' && (
        <>
          <div style={styles.albumGrid}>
            {/* All photos */}
            <button style={styles.albumCard} onClick={() => setView({ albumId: null, albumName: 'Tüm Fotoğraflar' })}>
              <div style={styles.albumCover}>
                {getCover(null)
                  ? <img src={getCover(null)!} alt="cover" style={styles.coverImg} />
                  : <span style={styles.placeholderIcon}>▧</span>}
              </div>
              <p style={styles.albumName}>Tüm Fotoğraflar</p>
              <p style={styles.albumCount}>{photos.length} fotoğraf</p>
            </button>

            {instantPhotos.length > 0 && (
              <button style={styles.albumCard} onClick={() => setView({ albumId: null, albumName: 'Günden kalan kareler', kind: 'instant' })}>
                <div style={styles.albumCover}>
                  {getCover(null, 'instant')
                    ? <img src={getCover(null, 'instant')!} alt="cover" style={styles.coverImg} />
                    : <span style={styles.placeholderIcon}>▧</span>}
                </div>
                <p style={styles.albumName}>Günden kalan kareler</p>
                <p style={styles.albumCount}>{instantPhotos.length} fotoğraf</p>
              </button>
            )}

            {albums.map(a => (
              <div key={a.id} style={{ position: 'relative' }}>
                <button style={styles.albumCard} onClick={() => setView({ albumId: a.id, albumName: a.name })}>
                  <div style={styles.albumCover}>
                    {getCover(a.id)
                      ? <img src={getCover(a.id)!} alt="cover" style={styles.coverImg} />
                      : <span style={styles.placeholderIcon}>□</span>}
                  </div>
                  <p style={styles.albumName}>{a.name}</p>
                  <p style={styles.albumCount}>{getAlbumPhotos(a.id).length} fotoğraf</p>
                </button>
                <button onClick={() => removeAlbum(a.id)} style={styles.albumDelete}>×</button>
              </div>
            ))}
          </div>

          <button className="fab" onClick={() => setShowAddAlbum(true)} style={{ bottom: 'calc(var(--nav-h) + 72px)' }}>
            □
          </button>
        </>
      )}

      {/* Photos in album */}
      {typeof view === 'object' && (
        <>
          <div style={styles.photoGrid}>
            {visiblePhotos.map(p => (
              <button key={p.id} style={styles.thumb} onClick={() => setSelected(p)}>
                <img src={p.url} alt={p.caption || ''} style={styles.thumbImg} loading="lazy" />
                <div style={styles.thumbOverlay}>
                  <span style={styles.thumbName}>{p.addedByName}</span>
                </div>
              </button>
            ))}
            {visiblePhotos.length === 0 && (
              <div style={styles.empty}>
                <span style={styles.emptyIcon}>▧</span>
                <p>Bu klasörde fotoğraf yok</p>
                <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 13 }}>Eklemek için + düğmesine bas</p>
              </div>
            )}
          </div>
        </>
      )}

      <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} style={{ display: 'none' }} />
      {typeof view === 'object' && !view.kind && <button className="fab" onClick={() => fileRef.current?.click()}>+</button>}

      {/* Photo detail */}
      {selected && (
        <div className="modal-overlay" onClick={() => setSelected(null)}>
          <div style={styles.photoModal} onClick={e => e.stopPropagation()}>
            <img src={selected.url} alt={selected.caption} style={styles.fullImg} />
            <div style={styles.photoMeta}>
              <div>
                <p style={{ fontWeight: 600 }}>{selected.addedByName}</p>
                {selected.albumName && <p style={{ fontSize: 12, color: '#A78BFA', marginTop: 2 }}>{selected.albumName}</p>}
                {selected.caption && <p style={{ color: 'rgba(255,255,255,0.6)', fontSize: 14, marginTop: 4 }}>{selected.caption}</p>}
              </div>
              <button onClick={() => removePhoto(selected)} style={styles.deleteBtn}>Sil</button>
            </div>
          </div>
        </div>
      )}

      {/* Add photo modal */}
      {showAddPhoto && (
        <div className="modal-overlay" onClick={!uploading ? closeAddPhoto : undefined}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 style={{ fontFamily: "'Playfair Display',serif", marginBottom: 14, fontSize: 20 }}>Fotoğraf Ekle</h2>
            {previewUrl && (
              <img src={previewUrl} alt="preview" style={{ width: '100%', borderRadius: 12, marginBottom: 12, maxHeight: 220, objectFit: 'cover' }} />
            )}
            {/* Album selector */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', display: 'block', marginBottom: 8 }}>Klasör</label>
              <div style={styles.albumChips}>
                <button
                  onClick={() => { setSelectedAlbumId(null); setSelectedAlbumName('Genel'); }}
                  style={{ ...styles.chip, ...(selectedAlbumId === null ? styles.chipActive : {}) }}
                >
                  Genel
                </button>
                {albums.map(a => (
                  <button
                    key={a.id}
                    onClick={() => { setSelectedAlbumId(a.id); setSelectedAlbumName(a.name); }}
                    style={{ ...styles.chip, ...(selectedAlbumId === a.id ? styles.chipActive : {}) }}
                  >
                    {a.name}
                  </button>
                ))}
              </div>
            </div>
            <input
              type="text" placeholder="Açıklama (isteğe bağlı)"
              value={caption} onChange={e => setCaption(e.target.value)}
              disabled={uploading} style={{ marginBottom: 12 }}
            />
            {uploadStatus && <p style={styles.status}>{uploadStatus}</p>}
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={closeAddPhoto} disabled={uploading} style={{ flex: 1 }}>İptal</button>
              <button className="btn btn-primary" onClick={upload} disabled={uploading} style={{ flex: 2 }}>
                {uploading ? 'Yükleniyor...' : 'Paylaş'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add album modal */}
      {showAddAlbum && (
        <div className="modal-overlay" onClick={() => setShowAddAlbum(false)}>
          <div className="modal-sheet" onClick={e => e.stopPropagation()}>
            <div className="modal-handle" />
            <h2 style={{ marginBottom: 14, fontSize: 20 }}>Yeni Klasör</h2>
            <input
              type="text" placeholder="Klasör adı (ör: Tatil, İlk Anlar...)"
              value={newAlbumName} onChange={e => setNewAlbumName(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && createAlbum()}
              style={{ marginBottom: 12 }}
            />
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-ghost" onClick={() => setShowAddAlbum(false)} style={{ flex: 1 }}>İptal</button>
              <button className="btn btn-primary" onClick={createAlbum} disabled={!newAlbumName.trim()} style={{ flex: 2 }}>
                Oluştur
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  title: { fontFamily: "'Inter',sans-serif", fontSize: 22, fontWeight: 850, letterSpacing: '-0.03em' },
  albumGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: '0 16px 90px' },
  albumCard: {
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8,
    background: 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.032))',
    border: '1px solid rgba(255,255,255,0.085)',
    borderRadius: 22, padding: 12, cursor: 'pointer',
    boxShadow: '0 18px 50px rgba(0,0,0,0.22)',
  },
  albumCover: {
    width: '100%', aspectRatio: '1', borderRadius: 17, overflow: 'hidden',
    background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  placeholderIcon: { fontSize: 34, color: 'rgba(255,255,255,0.34)', fontWeight: 900 },
  coverImg: { width: '100%', height: '100%', objectFit: 'cover' },
  albumName: { fontSize: 13, fontWeight: 850, color: 'white', textAlign: 'center' },
  albumCount: { fontSize: 11, color: 'rgba(255,255,255,0.4)' },
  albumDelete: {
    position: 'absolute', top: 8, right: 8, background: 'rgba(0,0,0,0.5)',
    border: 'none', color: 'rgba(255,255,255,0.6)', fontSize: 16, borderRadius: '50%',
    width: 24, height: 24, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  photoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 4, padding: '0 10px 90px' },
  thumb: {
    position: 'relative', aspectRatio: '1', overflow: 'hidden',
    cursor: 'pointer', background: 'rgba(255,255,255,0.05)', border: 'none', borderRadius: 14,
  },
  thumbImg: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  thumbOverlay: {
    position: 'absolute', bottom: 0, left: 0, right: 0,
    padding: '16px 8px 6px', background: 'linear-gradient(transparent, rgba(0,0,0,0.6))',
  },
  thumbName: { color: 'white', fontSize: 10, fontWeight: 600 },
  empty: {
    gridColumn: '1 / -1', display: 'flex', flexDirection: 'column', alignItems: 'center',
    gap: 8, padding: '60px 20px', color: 'rgba(255,255,255,0.5)', fontSize: 15, textAlign: 'center',
  },
  emptyIcon: { fontSize: 52, color: 'rgba(255,255,255,0.25)', fontWeight: 900 },
  photoModal: { position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column', justifyContent: 'center', padding: 16 },
  fullImg: { width: '100%', maxHeight: '70vh', objectFit: 'contain', borderRadius: 16, marginBottom: 12 },
  photoMeta: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    background: 'rgba(13,4,22,0.95)', borderRadius: 16, padding: '14px 16px',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  deleteBtn: { background: 'rgba(239,68,68,0.12)', border: '1px solid rgba(239,68,68,0.22)', borderRadius: 999, padding: '8px 12px', fontSize: 12, fontWeight: 800, cursor: 'pointer', color: '#FCA5A5' },
  albumChips: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  chip: {
    background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
    borderRadius: 20, padding: '6px 14px', fontSize: 13, color: 'rgba(255,255,255,0.7)', cursor: 'pointer',
  },
  chipActive: {
    background: 'linear-gradient(135deg, rgba(124,58,237,0.28), rgba(6,182,212,0.14))',
    border: '1px solid rgba(124,58,237,0.42)', color: 'white',
  },
  status: { textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.6)', marginBottom: 12, padding: 8, background: 'rgba(255,255,255,0.05)', borderRadius: 8 },
};
